import { Stack } from "@mui/material";
import {
  getSmsOptInDisclosure,
  isUsSmsSenderCountry,
  ORGANIZATION_COUNTRY_MAP,
  US_SMS_OPT_IN_DISCLOSURE,
} from "@simplyk/common";
import { FieldValues, Path, useFormContext } from "react-hook-form";

import { useFormV2Context } from "../../../contexts/FormV2Context";

import { isPhoneProvidedWhenOptedIn } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";
import { QuestionOutput } from "@/common/types/trpc";
import { FormCheckbox } from "@/forms-design-system/FormCheckbox";
import { FormPhoneInputField } from "@/forms-design-system/FormPhoneInputField";

interface Props<T extends FieldValues> {
  name: Path<T>;
  question: QuestionOutput;
}

export function QuestionFieldNotificationPhone<T extends FieldValues>({ name, question }: Props<T>) {
  const { t } = useTranslate();
  const { formCountry, formOrganization } = useFormV2Context();
  const form = useFormContext<T>();

  const phoneName = `${name}.answers.0` as Path<T>;
  const optInName = "smsOptIn" as Path<T>;

  const iso2Country = ORGANIZATION_COUNTRY_MAP[formCountry].countryCode;
  const phoneRules = [
    {
      validate: {
        phoneRequiredWhenOptedIn: (value: unknown) =>
          isPhoneProvidedWhenOptedIn(form.getValues(optInName), value, iso2Country)
            ? true
            : t("ticketing", "smsOptIn.phoneRequiredWhenOptedIn"),
      },
    },
  ];

  const handleOptInChange = () => {
    if (form.formState.submitCount > 0) {
      queueMicrotask(() => void form.trigger(phoneName));
    }
  };

  const optInCopy = isUsSmsSenderCountry(formCountry)
    ? { label: US_SMS_OPT_IN_DISCLOSURE, helperText: undefined }
    : {
        label: t("ticketing", "smsOptIn.friendlyLabel"),
        helperText: getSmsOptInDisclosure(formOrganization?.name ?? ""),
      };

  return (
    <Stack gap={1.5}>
      <FormPhoneInputField
        id={phoneName}
        control={form.control}
        name={phoneName}
        rules={phoneRules}
        label={t("ticketing", "smsOptIn.phoneLabel")}
        placeholder={t("ticketing", "smsOptIn.phonePlaceholder")}
        data-test="question-field-notification-phone"
        country={ORGANIZATION_COUNTRY_MAP[formCountry].phoneInputCountry}
        sx={{ width: "100%" }}
      />
      <input type="hidden" {...form.register(`${name}.questionId` as Path<T>)} value={question.id} />
      <FormCheckbox
        control={form.control}
        name={optInName}
        label={optInCopy.label}
        helperText={optInCopy.helperText}
        size="small"
        onChange={handleOptInChange}
        data-test="question-field-notification-phone-opt-in"
      />
    </Stack>
  );
}
